import { useState, useCallback, useEffect } from 'react';
import { api, type LinkedInItem } from '../api/client';
import { usePolling } from '../hooks/usePolling';

const TYPE_CONFIG: Record<string, { emoji: string; label: string; color: string }> = {
  connection_request: { emoji: '🤝', label: 'Connection Request', color: 'var(--accent)' },
  message:            { emoji: '💬', label: 'Message',            color: 'var(--primary)' },
  post_mention:       { emoji: '📣', label: 'Mentioned You',      color: 'var(--warning)' },
  comment:            { emoji: '💭', label: 'Comment',            color: 'var(--success)' },
};

export default function LinkedInScreen() {
  const [items, setItems] = useState<LinkedInItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [handling, setHandling] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setItems(await api.linkedin()); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  usePolling(load);

  const handle = async (id: string) => {
    setHandling(id);
    try {
      await api.handleLinkedIn(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } finally { setHandling(null); }
  };

  return (
    <div className="main-content">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800 }}>💼 LinkedIn</h1>
        {!loading && items.length > 0 && (
          <span style={{ color: 'var(--text-2)', fontSize: 14 }}>{items.length} pending</span>
        )}
      </div>

      {loading ? (
        <div className="card"><p style={{ color: 'var(--text-2)' }}>Loading…</p></div>
      ) : items.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-emoji">💼</div>
            <p className="empty-state-text">All caught up! Sync from Wardyn for new items.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((item) => {
            const cfg = TYPE_CONFIG[item.type] ?? { emoji: '📌', label: item.type, color: 'var(--text-2)' };
            return (
              <div key={item.id} className="card">
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                    background: cfg.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22,
                  }}>
                    {cfg.emoji}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span className="badge" style={{ background: cfg.color + '18', color: cfg.color, border: `1px solid ${cfg.color}40` }}>
                        {cfg.label}
                      </span>
                    </div>
                    {item.fromName && <div style={{ fontWeight: 600, fontSize: 14 }}>{item.fromName}</div>}
                    {item.fromTitle && <div style={{ color: 'var(--text-2)', fontSize: 13 }}>{item.fromTitle}</div>}
                    {item.snippet && <div style={{ color: 'var(--text-3)', fontSize: 13, fontStyle: 'italic', marginTop: 4 }}>{item.snippet}</div>}
                    {item.ai_sentiment && (
                      <span className="ai-pill" style={{ marginTop: 6 }}>✨ {item.ai_sentiment}</span>
                    )}
                    <button
                      className="btn-primary"
                      style={{ marginTop: 12, padding: '8px', fontSize: 13 }}
                      onClick={() => handle(item.id)}
                      disabled={handling === item.id}
                    >
                      {handling === item.id ? '…' : '✓ Mark as Handled'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
